import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { IListCentroCusto } from '../employee/models/list-centro-custo';
import { EmployeeService } from '../employee/services/employee.service';
import { Router, ActivatedRoute } from '@angular/router';
import { ToastService } from 'src/app/shared/services/toast.service';
import { ListCetroCustoService } from '../employee/services/list-centro-custo.service';
import { ICadastroEmployee } from '../employee/models/employee';
import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, finalize, switchMap, takeUntil } from 'rxjs/operators';
import { ResponseDefault, EMessageType } from 'src/app/shared/data/response-default.interface';
import { EMPLOYEE_STATUS_INFO, EnumEmployeeStatus } from 'src/app/shared/data/employee-status.enum';
import { ModalConfirmacaoService } from 'src/app/shared/services/modal-confirmacao.service';
import { SessionService } from 'src/app/shared/services/session.service';
import { EOrdenacao } from 'src/app/shared/data/ordenacao.enum';
import { ERoleAdminString } from 'src/app/shared/data/role.enum';
import { IFilial, IListByFilterFiliais } from '../../filiais/data/filiais';
import { FiliaisService } from '../../filiais/services/filiais.service';
import { CustomValidators } from 'src/app/shared/utilities/custom-validators';
import { cpfValidator } from 'src/app/shared/utilities/cpf-validator';
import { interceptorAvisouErro } from 'src/app/shared/http-interceptor/http-interceptor.module';

@Component({
  selector: 'app-edit-employee',
  templateUrl: './edit-employee.component.html',
  styleUrls: ['./edit-employee.component.scss']
})
export class EditEmployeeComponent implements OnInit, OnDestroy {
  ERoleAdminString = ERoleAdminString;
  employeeId?: string | null;
  /** Empresa da filial escolhida; cada valor novo cancela a busca de centros de custo anterior. */
  private readonly empresaDaFilial$ = new Subject<string | undefined>();
  private readonly destroy$ = new Subject<void>();

  opcoesFiliais: IFilial[] = [];
  opcoesCentroCustos: IListCentroCusto[] = [];
  opcoesStatus = EMPLOYEE_STATUS_INFO;

  dadosColaborador?: ICadastroEmployee;

  formEditEmployee = new UntypedFormGroup({
    status: new UntypedFormControl(null, [Validators.required]),
    active: new UntypedFormControl('', [Validators.required]),
    name: new UntypedFormControl(null, [
      Validators.required,
      Validators.maxLength(150),
      Validators.pattern(/^[a-zA-ZÀ-ÿ]+(?:['-][a-zA-ZÀ-ÿ]+)*(?:\s+[a-zA-ZÀ-ÿ]+(?:['-][a-zA-ZÀ-ÿ]+)*)+$/)
    ]),
    email: new UntypedFormControl(null, [CustomValidators.requiredTrimmed, CustomValidators.email]),
    // Obrigatória só quando a situação não é Candidato (regra ligada no ngOnInit).
    registrationNumber: new UntypedFormControl(null),
    document: new UntypedFormControl(null, [Validators.required, cpfValidator]),
    phone: new UntypedFormControl(null, [Validators.required]),
    id: new UntypedFormControl(null),
    role: new UntypedFormControl(null, [
      CustomValidators.requiredTrimmed,
      Validators.maxLength(100),
      Validators.pattern(/^[a-zA-ZÀ-ÿ\s\(\)]+$/)
    ]),
    subsidiaryId: new UntypedFormControl(null, Validators.required),
    companyId: new UntypedFormControl(null, Validators.required),
    costCenterId: new UntypedFormControl(null, [Validators.required])
  });

  /** Trava o botão enquanto o salvamento está em andamento, para não enviar a edição duas vezes. */
  salvando = false;

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly employeeservice: EmployeeService,
    private readonly toastService: ToastService,
    private readonly listCentroCustoService: ListCetroCustoService,
    private readonly filiaisService: FiliaisService,
    private readonly sessionService: SessionService,
    private readonly modalConfirmacao: ModalConfirmacaoService
  ) {}

  public ngOnInit(): void {
    this.employeeId = this.activatedRoute.snapshot.paramMap.get('id');

    this.formEditEmployee
      .get('status')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((status) => {
        const matricula = this.formEditEmployee.get('registrationNumber')!;
        matricula.setValidators(status !== EnumEmployeeStatus.Candidato ? [CustomValidators.requiredTrimmed] : []);
        matricula.updateValueAndValidity({ emitEvent: false });
      });

    this.empresaDaFilial$
      .pipe(
        switchMap((companyId) => (companyId ? this.buscarCentrosCusto(companyId) : EMPTY)),
        takeUntil(this.destroy$)
      )
      .subscribe((resposta) => (this.opcoesCentroCustos = resposta.data));

    if (this.employeeId) {
      this.carregarDados();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Matrícula vira obrigatória quando a situação não é Candidato. */
  get matriculaObrigatoria(): boolean {
    return !!this.formEditEmployee.get('registrationNumber')?.hasValidator(CustomValidators.requiredTrimmed);
  }

  /** X: com dados alterados, pergunta antes de descartar, como no cadastro. */
  fechar(): void {
    this.modalConfirmacao.confirmarDescarte(this.formEditEmployee).subscribe((podeFechar) => {
      if (podeFechar) this.router.navigateByUrl('/dash/colaboradores');
    });
  }

  editEmployee(): void {
    if (this.salvando) return;

    if (this.formEditEmployee.valid) {
      const colaborador = this.formEditEmployee.getRawValue();
      colaborador.name = this.sanitizeText(colaborador.name);
      colaborador.role = this.sanitizeText(colaborador.role);
      colaborador.email = colaborador.email?.trim().toLowerCase();

      this.salvando = true;
      this.employeeservice
        .editarColaborador({ ...colaborador, createAccountUser: false })
        .pipe(finalize(() => (this.salvando = false)))
        .subscribe({
          next: (res) => {
            if (res.messages && res.messages.length > 0) {
              this.toastService.toast(res.messages);
            }
            if (res.isSucceed) {
              this.router.navigateByUrl('/dash/colaboradores');
            }
          },
          error: (erro) => {
            if (!interceptorAvisouErro(erro)) {
              this.toastService.toast('Erro ao salvar o colaborador. Tente novamente.', EMessageType.Error);
            }
          }
        });
    } else {
      const validationErrors = this.getValidationErrors();
      if (validationErrors) {
        this.toastService.toast(validationErrors, EMessageType.Warning);
      }
    }
  }

  private getValidationErrors(): string {
    const fieldLabels: { [key: string]: string } = {
      name: 'Nome',
      email: 'E-mail',
      registrationNumber: 'Matrícula/ID',
      document: 'CPF',
      phone: 'Telefone',
      role: 'Função',
      subsidiaryId: 'Filial',
      costCenterId: 'Centro de Custo',
      status: 'Status',
      active: 'Ativo'
    };

    const errors: string[] = [];

    Object.keys(this.formEditEmployee.controls).forEach((key) => {
      const control = this.formEditEmployee.get(key);
      if (control && control.invalid) {
        const fieldLabel = fieldLabels[key] || key;
        const controlErrors = control.errors;

        if (controlErrors) {
          if (controlErrors['required']) {
            errors.push(`${fieldLabel} é obrigatório`);
          } else if (controlErrors['email']) {
            errors.push('E-mail inválido');
          } else if (controlErrors['pattern']) {
            if (key === 'name') {
              errors.push(`${fieldLabel} deve conter apenas letras, acentos e hífens`);
            } else if (key === 'role') {
              errors.push(`${fieldLabel} deve conter apenas letras, espaços e parênteses`);
            } else {
              errors.push(`${fieldLabel} tem formato inválido`);
            }
          } else if (controlErrors['maxlength']) {
            errors.push(`${fieldLabel} excede o número máximo de caracteres`);
          } else if (controlErrors['cpf']) {
            errors.push('CPF inválido');
          } else if (controlErrors['mask']) {
            // Erro do ngx-mask (valor incompleto): sem este ramo o salvar travava sem mensagem.
            errors.push(`${fieldLabel} tem formato inválido`);
          }
        }
      }
    });

    return errors.length > 0 ? errors.join('\n') : '';
  }

  private sanitizeText(value: string | null | undefined): string | null {
    return value?.trim().replace(/\s+/g, ' ') ?? null;
  }
  carregarDados(): void {
    this.getListFilial()
      .pipe(
        switchMap((resposta) => {
          this.opcoesFiliais = resposta.data;
          return this.carregarDetalhesColaborador();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (resposta) => {
          if (resposta.messages && resposta.messages.length > 0) {
            this.toastService.toast(resposta.messages);
          }
          if (resposta.isSucceed) {
            this.dadosColaborador = resposta.data;
            this.opcoesFiliais.push(resposta.data.subsidiary);

            this.formEditEmployee.patchValue({
              id: this.employeeId,
              name: resposta.data.name,
              active: resposta.data.active,
              email: resposta.data.email,
              registrationNumber: resposta.data.registrationNumber,
              document: resposta.data.document,
              phone: resposta.data.phone,
              costCenterId: resposta.data.costCenterId,
              role: this.sanitizeText(resposta.data.role),
              status: resposta.data.status,
              subsidiaryId: resposta.data.subsidiaryId,
              companyId: resposta.data.subsidiary.companyId
            });

            this.empresaDaFilial$.next(resposta.data.subsidiary.companyId);
          }
        },
        error: (erro) => {
          if (!interceptorAvisouErro(erro)) {
            this.toastService.toast('Erro ao carregar os dados do colaborador. Tente novamente.', EMessageType.Error);
          }
        }
      });
  }

  resendAccessApp(): void {
    this.employeeservice.reenviarAcessoApp(this.employeeId!).subscribe(
      (res) => {
        if (res.messages && res.messages.length > 0) {
          this.toastService.toast(res.messages);
        }
      },
      (error) => {
        this.toastService.toast(
          'Erro ao enviar o acesso à carteirinha. Tente novamente mais tarde.',
          EMessageType.Error
        );
      }
    );
  }

  changeFilial(): void {
    const selectedFilial = this.opcoesFiliais.find(
      (filial) => filial.id === this.formEditEmployee.get('subsidiaryId')?.value
    );

    // O centro de custo escolhido é da empresa anterior. Filial limpa: só cancela a busca pendente.
    if (selectedFilial) {
      this.formEditEmployee.get('costCenterId')?.patchValue(null);
    }
    this.empresaDaFilial$.next(selectedFilial?.companyId);
  }

  /** Falha na busca: limpa as opções e avisa, sem encerrar o stream das próximas trocas de filial. */
  private buscarCentrosCusto(companyId: string): Observable<ResponseDefault<IListCentroCusto[]>> {
    return this.getListCentroCusto(companyId).pipe(
      catchError((erro) => {
        this.opcoesCentroCustos = [];
        if (!interceptorAvisouErro(erro)) {
          this.toastService.toast('Erro ao carregar os centros de custo. Tente novamente.', EMessageType.Error);
        }
        return EMPTY;
      })
    );
  }

  getListCentroCusto(companyId?: string): Observable<ResponseDefault<IListCentroCusto[]>> {
    return this.listCentroCustoService.getCentroCusto(companyId);
  }

  getListFilial(): Observable<ResponseDefault<IFilial[]>> {
    const filtros: IListByFilterFiliais = {
      orderBy: EOrdenacao.AlfabeticaAZ,
      pageNumber: 0,
      pageSize: 9999
    };

    const partnerId = this.sessionService.getSession().partnerId;
    const companyId = this.sessionService.getSession().companyId;
    const accountRole = this.sessionService.getSession().accountRole;

    if (partnerId) {
      filtros.partnerId = partnerId;
    }

    if (companyId) {
      filtros.companyId = companyId;
    }

    if (accountRole) {
      filtros.accountRole = parseInt(accountRole);
    }

    return this.filiaisService.listarTodasFiliais(filtros);
  }

  public carregarDetalhesColaborador(): Observable<ResponseDefault<ICadastroEmployee>> {
    return this.employeeservice.detalhesColaborador(this.employeeId!);
  }
}
